import React from "react";
import { useEffect } from "react";
import { useLoaderData } from "@tanstack/react-router";
import { CaseStudyDetailHeroSection } from "@tennr/frontend/sections/CaseStudyDetailHeroSection";
import { CaseStudyDetailContentSection } from "@tennr/frontend/sections/CaseStudyDetailContentSection";
import { CaseStudyDetailMetricsSection } from "@tennr/frontend/sections/CaseStudyDetailMetricsSection";
import { PageBackground } from "@tennr/frontend/components/PageBackground";
import { SiteFooter } from "@tennr/frontend/components/SiteFooter";
import { MetadataTags } from "@tennr/frontend/components/MetadataTags";
import "@tennr/style/index.css";
import "./CaseStudyDetailPage.css";

const bgImage = "/img/case-study-hero-bg.jpg";
const bgImageVert = "/img/case-study-hero-bg-vert.jpg";

export const CaseStudyDetailPage: React.FC = () => {
  const { caseStudy } = useLoaderData({ from: "/_app/customer-stories/$caseStudyId" });

  useEffect(() => {
    const preload = (src?: string) => {
      if (!src) return;
      const img = new Image();
      img.src = src;
    };

    const isMobile = window.innerWidth <= 768 || window.matchMedia("(orientation: portrait)").matches;

    if (isMobile) {
      // Preload vertical/mobile images
      preload("/img/homepage-hero-bg-vert2.jpg");
      preload("/img/product-hero-bg-vert2.jpg");
      preload("/img/customer-stories-hero-bg-vert.jpg");
      preload("/img/about-us-hero-bg-vert.jpg");
      preload("/img/case-study-hero-bg-vert.jpg");
      preload("/img/book-demo-hero-bg-vert.jpg");
    } else {
      // Preload desktop images
      preload("/img/homepage-hero-bg.jpg");
      preload("/img/product-hero-bg.jpg");
      preload("/img/customer-stories-hero-bg.jpg");
      preload("/img/about-us-hero-bg.jpg");
      preload("/img/case-study-hero-bg.jpg");
      preload("/img/book-demo-hero-bg.jpg");
    }

    preload("/img/grain.png"); // always used
  }, []);

  if (!caseStudy) {
    return (
      <main className="case-study-detail-page">
        <div className="case-study-detail-error">
          <h1>Case Study Not Found</h1>
          <p>The case study you're looking for doesn't exist or has been removed.</p>
          <a href="/customer-stories">View all case studies</a>
        </div>
      </main>
    );
  }

  const seoDesc =
    "Learn how Tennr helped " +
    caseStudy.company +
    " convert more patients, reduce denials, and do it all with fewer resources.";
  const seoTitle = caseStudy.company + " Case Study | Tennr";

  return (
    <>
      <MetadataTags
        title={seoTitle}
        description={seoDesc}
        imageUrle="https://tennr.com/share_cards/sharecard-stories.webp"
      />
      <main className="case-study-detail-page">
        <PageBackground bgImage={bgImage} bgImageVert={bgImageVert} />
        <CaseStudyDetailHeroSection caseStudy={caseStudy} />
        <CaseStudyDetailContentSection caseStudy={caseStudy} />
        {caseStudy.metrics && caseStudy.metrics.length > 0 && (
          <CaseStudyDetailMetricsSection metrics={caseStudy.metrics} />
        )}
      </main>
      <SiteFooter bgImage="/img/footer-case-study-bg.jpg" bgImageVert="/img/footer-case-study-bg-vert.jpg" />
    </>
  );
};
